import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { environment } from '../../../environments/environment'
import { OrganizationUserPermission } from '../../shared/classes/organization-user-permission'
import { RestrictedProductAccountUser } from '../../shared/classes/restricted-product-account-user'
import {
  InviteOrganizationUser,
  Notification,
  Organization,
  OrganizationPolicy,
  PaginatedList,
  PaginatedInvitesResponse,
  RestrictedProductAccountUserNotification,
  User,
  UserPermissionBase,
  CompanySubscription,
} from '../../shared/shared.interfaces'
import { head } from 'lodash'

@Injectable({
  providedIn: 'root',
})
export class UserPermissionsService {
  constructor(private httpClient: HttpClient) {}

  /*istanbul ignore next*/
  public getCompanyUsers(currentPage: number, pageSize: number): Observable<PaginatedList<User>> {
    return this.httpClient
      .get(`${environment.usersUrl}?pageIndex=${currentPage}&pageSize=${pageSize}`)
      .pipe(map((response) => response as PaginatedList<User>))
  }

  /*istanbul ignore next*/
  organization(id: number): Observable<Organization> {
    return this.httpClient.get<Organization>(`${environment.organizationsUrl}/${id}`)
  }

  /*istanbul ignore next*/
  getOrganizationUsers(organizationId: number): Observable<Array<OrganizationUserPermission>> {
    return this.httpClient
      .get<Array<OrganizationUserPermission>>(`${environment.organizationsUrl}/${organizationId}/users`)
      .pipe(
        map((organizationUserPermissions) => {
          return organizationUserPermissions.map((item) => {
            return Object.assign(new OrganizationUserPermission(), item)
          })
        }),
      )
  }

  /*istanbul ignore next*/
  organizationUser(organizationId: number, userId: number) {
    return this.httpClient
      .get<OrganizationUserPermission>(`${environment.organizationsUrl}/${organizationId}/users/${userId}`)
      .pipe(
        map((organizationUserPermission) => {
          return Object.assign(new OrganizationUserPermission(), organizationUserPermission)
        }),
      )
  }

  /*istanbul ignore next*/
  accountPermissions(
    userId: number,
    organizationId: number,
    currentPage: number = 1,
    pageSize: number = 999,
  ): Observable<RestrictedProductAccountUser[]> {
    return this.httpClient
      .get<PaginatedList<RestrictedProductAccountUser>>(
        // tslint:disable-next-line: max-line-length
        `${environment.organizationsUrl}/${organizationId}/users/${userId}/restrictedProductAccountPermissions?pageIndex=${currentPage}&pageSize=${pageSize}`,
      )
      .pipe(map((list) => list.items.map((item) => Object.assign(new RestrictedProductAccountUser(), item))))
  }

  /*istanbul ignore next*/
  getAllAccountsWithNotificationsForLoggedInUser(
    organizationId: number,
    currentPage: number = 1,
    pageSize: number = 999,
  ): Observable<RestrictedProductAccountUserNotification[]> {
    return this.httpClient
      .get<PaginatedList<RestrictedProductAccountUserNotification>>(
        // tslint:disable-next-line: max-line-length
        `${environment.usersUrl}/organizations/${organizationId}/Notifications?pageIndex=${currentPage}&pageSize=${pageSize}`,
      )
      .pipe(
        map((notifications) => {
          const items = notifications.items.map((item) => ({
            ...item,
            productOrdered: item.userNotifications[item.userNotifications.findIndex((x) => x.id === 1)],
            accessRequested: item.userNotifications[item.userNotifications.findIndex((x) => x.id === 2)],
            accessGranted: item.userNotifications[item.userNotifications.findIndex((x) => x.id === 3)],
            expirationExtensionRequest: item.userNotifications[item.userNotifications.findIndex((x) => x.id === 4)],
            authorizationPeriodExtended: item.userNotifications[item.userNotifications.findIndex((x) => x.id === 5)],
            authorizationPeriodExpiringSoon:
              item.userNotifications[item.userNotifications.findIndex((x) => x.id === 6)],
            authorizationCreated: item.userNotifications[item.userNotifications.findIndex((x) => x.id === 7)],
          }))
          return items
        }),
      )
  }

  /*istanbul ignore next*/
  addNotification(restrictedProductAccountId: number, notification: Notification) {
    return this.httpClient.post(
      `${environment.usersUrl}/restrictedProductAccounts/${restrictedProductAccountId}/notifications`,
      notification,
    )
  }

  /*istanbul ignore next*/
  removeNotification(restrictedProductAccountId: number, notificationId: number) {
    return this.httpClient.delete(
      // tslint:disable-next-line: max-line-length
      `${environment.usersUrl}/restrictedProductAccounts/${restrictedProductAccountId}/notifications/${notificationId}`,
    )
  }

  /*istanbul ignore next*/
  addOrganizationPermission(organizationId: number, userId: number, payload) {
    return this.httpClient.post(
      `${environment.organizationsUrl}/${organizationId}/users/${userId}/permissions`,
      payload,
    )
  }

  /*istanbul ignore next*/
  updateOrganizationUserPermission(organizationId: number, userId: number, payload: UserPermissionBase) {
    return this.httpClient.put<void>(
      `${environment.organizationsUrl}/${organizationId}/users/${userId}/permissions`,
      payload,
    )
  }

  /*istanbul ignore next*/
  removeAdmin(organizationId: number, userId: number, permissionId: number) {
    return this.httpClient.delete(
      `${environment.organizationsUrl}/${organizationId}/users/${userId}/permissions/${permissionId}`,
    )
  }

  /*istanbul ignore next*/
  addPermission(userId: number, restrictedProductAccountId: number, permission: UserPermissionBase) {
    return this.httpClient.post<void>(
      `${environment.restrictedProductUrl}/${restrictedProductAccountId}/users/${userId}/permissions`,
      permission,
    )
  }

  /*istanbul ignore next*/
  removePermission(userId: number, restrictedProductAccountId: number, permissionId: number) {
    return this.httpClient.delete<void>(
      // tslint:disable-next-line: max-line-length
      `${environment.restrictedProductUrl}/${restrictedProductAccountId}/users/${userId}/permissions/${permissionId}`,
    )
  }

  /*istanbul ignore next*/
  updatePermission(userId: number, restrictedProductAccountId: number, permission: UserPermissionBase) {
    return this.httpClient.put<void>(
      // tslint:disable-next-line: max-line-length
      `${environment.restrictedProductUrl}/${restrictedProductAccountId}/users/${userId}/permissions`,
      permission,
    )
  }

  /*istanbul ignore next*/
  removeOrganizationUser(organizationId: number, userId: number) {
    return this.httpClient.delete(`${environment.organizationsUrl}/${organizationId}/users/${userId}`)
  }

  /*istanbul ignore next*/
  addOrganizationUser(organizationId: number, payload: InviteOrganizationUser) {
    return this.httpClient.post(`${environment.organizationsUrl}/${organizationId}/users`, payload)
  }

  /*istanbul ignore next*/
  addBulkPermissions(organizationId: number, userId: number, permission: UserPermissionBase) {
    return this.httpClient.post(
      `${environment.organizationsUrl}/${organizationId}/users/${userId}/restrictedProductAccountPermissions`,
      permission,
    )
  }

  /*istanbul ignore next*/
  removeBulkPermissions(organizationId: number, userId: number, permissionId: number) {
    return this.httpClient.delete(
      `${environment.organizationsUrl}/${organizationId}/users/${userId}/restrictedProductAccountPermissions/${permissionId}`,
    )
  }

  /*istanbul ignore next*/
  getUserOrganizationPermissions(organizationId: number): Observable<OrganizationPolicy> {
    return this.httpClient.get<OrganizationPolicy>(
      `${environment.usersUrl}/organizations/${organizationId}/permissions`,
    )
  }

  /*istanbul ignore next*/
  updateOrganization(organizationId: number, payload: Organization) {
    return this.httpClient.put<void>(`${environment.organizationsUrl}/${organizationId}`, payload)
  }

  /*istanbul ignore next*/
  searchCRMAccount(accountNumber: number): Observable<any> {
    return this.httpClient.get<any>(`${environment.companyUrl}?accountNumber=${accountNumber}`)
  }

  /*istanbul ignore next*/
  linkCompany(organizationId: number, companyId: number, branchId: number) {
    const url = `${environment.baseUrl}${environment.version1}/Organizations/${organizationId}/Company/${companyId}/Branch/${branchId}`

    return this.httpClient.patch<any>(url, null)
  }

  /*istanbul ignore next*/
  addKSUser(branchId: number, payload: any) {
    return this.httpClient.post<any>(
      `${environment.baseUrl}${environment.version1}/branches/${branchId}/users`,
      payload,
    )
    // return this.httpClient.post<any>(`${environment.ksUrl}`, payload);
  }

  /*istanbul ignore next*/
  updateUserRole(branchId, userId, roleId) {
    const payload = {
      resourceId: branchId,
      resourceType: 'keystructuregroup',
      userId: userId,
      role: roleId,
    }
    return this.httpClient.patch<any>(
      `${environment.baseUrl}${environment.version1}/branches/${branchId}/users`,
      payload,
    )
  }

  /*istanbul ignore next*/
  removeKSUser(branchId: number, userId: number) {
    const payload = {
      userId: userId,
      resourceId: branchId,
      resourceType: 'keystructuregroup',
    }
    const options = {
      body: payload,
    }
    return this.httpClient.delete(`${environment.baseUrl}${environment.version1}/branches/${branchId}/users`, options)
  }

  /*istanbul ignore next*/
  public getRegisteredUsers(branchId: number): Observable<any[]> {
    const headers = new HttpHeaders({
      Accept: 'application/allegion.keying.get.users.by.branch.id+json',
    })
    return this.httpClient.get<User[]>(`${environment.baseUrl}${environment.version1}/branches/${branchId}/users`, {
      headers,
    })
    // return this.httpClient.get<User[]>(`${environment.baseUrl}${environment.version1}/branches/541630/users`, {
    //   headers,
    // })
  }

  /*istanbul ignore next*/
  public getPendingUsers(
    branchId: number,
    pageSize: number = 10,
    pageNumber: number = 1,
  ): Observable<PaginatedInvitesResponse> {
    const headers = new HttpHeaders({
      Accept: 'application/allegion.keying.get.invited.users.by.branch.id+json',
    })
    const params = new HttpParams().set('pageSize', pageSize.toString()).set('pageNumber', pageNumber.toString())
    return this.httpClient.get<PaginatedInvitesResponse>(
      `${environment.baseUrl}${environment.version1}/branches/${branchId}/users`,
      {
        headers,
        params,
      },
    )
  }

  /*istanbul ignore next*/
  public getStructures(branchId: number): Observable<any[]> {
    return this.httpClient.get<any[]>(`${environment.baseUrl}${environment.version1}/branches/${branchId}/structures`)
  }

  /*istanbul ignore next*/
  public getProjectsForStructure(branchId: number, structureId: number): Observable<any[]> {
    return this.httpClient.get<any[]>(
      `${environment.baseUrl}${environment.version1}/branches/${branchId}/ProjectStructure/structures/${structureId}`,
    )
  }

  /*istanbul ignore next*/
  searchKSStructure(structureNumber: string): Observable<any> {
    const url = `${environment.baseUrl}${environment.version1}/Structures`
    const params = new HttpParams().set('structureNumber', structureNumber)
    return this.httpClient.get<any>(url, { params })
  }

  /*istanbul ignore next*/
  addKSStructure(structureId: number, branchId: number) {
    const payload = {
      structureId: structureId,
      branchId: branchId,
    }
    return this.httpClient.post<any>(`${environment.baseUrl}${environment.version1}/Structures`, payload)
  }

  /*istanbul ignore next*/
  searchProject(id: number): Observable<any> {
    return this.httpClient.get<any>(`${environment.baseUrl}${environment.version1}/Projects/${id}`)
  }

  /*istanbul ignore next*/
  linkProject(branchId: number, projectId: number, structureNumber: string) {
    const payload = {
      projectId: projectId,
      structureNumber: structureNumber,
    }
    return this.httpClient.post<any>(
      `${environment.baseUrl}${environment.version1}/branches/${branchId}/ProjectStructure`,
      payload,
    )
  }

  /*istanbul ignore next*/
  removeProject(branchId: number, structureId: number, projectId: number) {
    return this.httpClient.delete<any>(
      `${environment.baseUrl}${environment.version1}/branches/${branchId}/ProjectStructure/Project/${projectId}/Structure/${structureId}`,
    )
  }

  /*istanbul ignore next*/
  getUserRoles(branchId: number): Observable<any> {
    return this.httpClient.get<any>(`${environment.baseUrl}${environment.version1}/branches/${branchId}/roles`).pipe(
      map((roles) => {
        if (roles.roles) {
          roles.roles = this.addRoleDetails(roles.roles).sort((a, b) => a.order - b.order)
        }
        return roles
      }),
      catchError((error) => {
        console.error('Error fetching user roles:', error)
        return of(null)
      }),
    )
  }

  private addRoleDetails(roles: any[]): any[] {
    return roles.map((role) => {
      switch (role.id) {
        case 'viewonly':
          role.order = 1
          role.description =
            'Users with this role can view key systems, openings, and keyholders, but cannot edit them or add/delete.'
          break
        case 'designer':
          role.order = 2
          role.description =
            'Users with this role can view and create key systems, create and delete keys, associate keys to openings, create notes calculate budgets, and print Master Key orders.'
          break
        case 'keyissuer':
          role.order = 3
          role.description =
            'Users with this role can add, import, or remove keyholders, issue or return keys or key rings, and upload documents.'
          break
        case 'locksmith':
          role.order = 4
          role.description =
            'Users with this role can export bitting and pinning reports, create or delete new keys or key rings and request additional bittings, and associate keys to openings.'
          break
        case 'fullaccess':
          role.order = 5
          role.description =
            'Users with this role can perform all of the actions from the roles above. They can also create serialization accounts.'
          break
        case 'branchadmin':
          role.order = 6
          role.description =
            'This user acts as the administrator for the company. They can see all restricted product accounts and key structures associated with the company and perform all actions within the application including inviting other users to the company and determining their role.'
          break
        default:
          role.description = 'No specific role description available.'
          break
      }
      return role
    })
  }

  /*istanbul ignore next*/
  getCompanyByOrg(orgId: number): Observable<any> {
    return this.httpClient.get<any>(`${environment.baseUrl}${environment.version1}/Organizations/${orgId}/Company`)
    // return this.httpClient.get<any>(`${environment.ksUrl}`);
  }

  /*istanbul ignore next*/
  getCompanySubscription(branchId: number): Observable<CompanySubscription> {
    return this.httpClient.get<CompanySubscription>(
      `${environment.baseUrl}${environment.version1}/Companies/Branches/${branchId}/Subscription`,
    )
  }

  /*istanbul ignore next*/
  checkKSPermission(resourceType: string, permission: string, branchId?: string): Observable<any> {
    const body = {
      resourceId: branchId,
      resourceType: resourceType,
      permission: permission,
    }
    return this.httpClient.post<any>(`${environment.oksmUrl}`, body)
  }
}
